import httpClient, { TOKEN_KEY } from './http-client'
import type {
  LoginCredentials,
  RegisterCredentials,
  PasswordResetRequestCredentials,
  ResetPasswordCredentials,
  AuthResponse,
  UserResponse,
  User,
} from '../types/auth'

class AuthService {
  /**
   * Register a new user
   */
  async register(credentials: RegisterCredentials): Promise<string> {
    const response = await httpClient.post<AuthResponse>('/auth/register', credentials)
    const token = response.data.data.token

    // Store token
    this.setToken(token)

    return token
  }

  /**
   * Login existing user
   */
  async login(credentials: LoginCredentials): Promise<string> {
    const response = await httpClient.post<AuthResponse>('/auth/login', credentials)
    const token = response.data.data.token

    // Store token
    this.setToken(token)

    return token
  }

  /**
   * Confirm user email with token
   */
  async confirmEmail(token: string): Promise<string> {
    const response = await httpClient.post<AuthResponse>('/auth/confirm', { token })
    const loginToken = response.data.data.token

    // Store token
    this.setToken(loginToken)

    return loginToken
  }

  /**
   * Request a password reset email
   */
  async requestPasswordReset(credentials: PasswordResetRequestCredentials): Promise<void> {
    await httpClient.post('/auth/password/reset-request', credentials)
  }

  /**
   * Reset password with emailed token
   */
  async resetPassword(credentials: ResetPasswordCredentials): Promise<string> {
    const response = await httpClient.post<AuthResponse>('/auth/password/reset', credentials)
    const loginToken = response.data.data.token

    this.setToken(loginToken)

    return loginToken
  }

  /**
   * Logout current user
   */
  logout(): void {
    this.removeToken()
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    const response = await httpClient.get<UserResponse>('/users/me')
    return response.data.data
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken()
  }

  /**
   * Get JWT token from storage
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY)
  }

  /**
   * Store JWT token
   */
  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token)
  }

  /**
   * Remove JWT token from storage
   */
  removeToken(): void {
    localStorage.removeItem(TOKEN_KEY)
  }
}

export default new AuthService()
